import { Col, Row as AntRow } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  CreateBranchingDetailsProps,
  FormQuestion,
  FormTemplate,
} from 'api/form/types';
import { VariablesContext } from 'contexts/variables';
import { useScrollTop } from 'hooks/use-scroll-top';
import { computeFormBgImageBrightness } from '../utils';
import { FormBuilderConfiguration } from './form-builder-configuration';
import { FormBuilderPreview } from './form-builder-preview';
import { FormQuestions } from './form-questions';

interface Props {
  formTemplate: FormTemplate;
}

const LeftColWidth = 256;
const RightColWidth = 360;
const Row = styled(AntRow)`
  background: var(--coolest-gray-20);
  height: 100%;
`;

const LeftCol = styled(Col)`
  background: var(--white-100);
  border-right: solid 1px var(--border-color);
  width: ${LeftColWidth}px;
  max-width: ${LeftColWidth}px;
  height: 100%;
`;

const CenterCol = styled(Col)<{
  backgroundColor: string;
  backgroundImage: string;
}>`
  width: calc(100% - ${LeftColWidth}px - ${RightColWidth}px);
  background-color: ${(props) =>
    !props.backgroundImage && `${props.backgroundColor}`};

  overflow: scroll;
  height: 100%;
`;
const RightCol = styled(Col)`
  border-left: solid 1px var(--border-color);
  width: ${RightColWidth}px;
  background: var(--white-100);
`;

const CenterColContent = styled.div<{
  backgroundImage: string;
  backgroundBrightnessOverlay: string;
}>`
  background: ${(props) =>
    props.backgroundImage &&
    `url(${props.backgroundImage}) no-repeat center center fixed`};
  background-size: cover;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    background-color: ${(props) =>
      props.backgroundImage &&
      props.backgroundBrightnessOverlay &&
      `${props.backgroundBrightnessOverlay}`};
  }
`;

export const FormBuilder: FC<Props> = ({ formTemplate }) => {
  const [selectedFormItem, setSelectedFormItem] =
    useState<number | FormQuestion | null>(null);

  const [createBranchingDetails, setCreateBranchingDetails] =
    useState<CreateBranchingDetailsProps | null>(null);

  const [createNewQuestionLoading, setCreateNewQuestionLoading] =
    useState(false);

  const [centerColWrapperRef, isCenterColScrolled] = useScrollTop();

  const brightnesssOverlay = useMemo(() => {
    return formTemplate.formTheme.brightness
      ? computeFormBgImageBrightness(formTemplate.formTheme.brightness)
      : '';
  }, [formTemplate.formTheme.brightness]);

  return (
    <Row>
      <LeftCol>
        <FormQuestions
          templateId={formTemplate.templateId}
          setCreateNewQuestionLoading={setCreateNewQuestionLoading}
          setSelectedFormItem={setSelectedFormItem}
        />
      </LeftCol>
      <CenterCol
        ref={centerColWrapperRef}
        backgroundImage={formTemplate.formTheme.backgroundImage || ''}
        backgroundColor={formTemplate.formTheme.backgroundColor}
      >
        <CenterColContent
          backgroundImage={formTemplate.formTheme.backgroundImage || ''}
          backgroundBrightnessOverlay={brightnesssOverlay}
        >
          <VariablesContext.Provider
            value={{
              allowSimpleVariables: true,
            }}
          >
            <FormBuilderPreview
              centerColWrapperRef={centerColWrapperRef}
              isCenterColScrolled={isCenterColScrolled}
              formTemplate={formTemplate}
              selectedFormItem={selectedFormItem}
              setSelectedFormItem={setSelectedFormItem}
              createBranchingDetails={createBranchingDetails}
              setCreateBranchingDetails={setCreateBranchingDetails}
              createNewQuestionLoading={createNewQuestionLoading}
            />
          </VariablesContext.Provider>
        </CenterColContent>
      </CenterCol>
      <RightCol>
        <FormBuilderConfiguration
          formTemplate={formTemplate}
          selectedFormItem={selectedFormItem}
          setSelectedFormItem={setSelectedFormItem}
          setCreateBranchingDetails={setCreateBranchingDetails}
        />
      </RightCol>
    </Row>
  );
};
